import { useTranslation } from 'react-i18next';
import { translateApiErrorMessage } from '@/utils/translateMessage';
import React, { useRef, useEffect, forwardRef, useImperativeHandle, useId } from 'react';
import { Input } from "@/components/ui/input";
import { ErpDiv, ErpLabelRaw, ErpP, ErpSpan } from './ErpHtml';

export interface ErpFormInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'size'> {
  value?: string | number | null; // Replaces modelValue
  type?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  size?: 'sm' | 'lg';
  state?: boolean | null;
  isInvalid?: boolean;
  isValid?: boolean;
  id?: string;
  formatter?: (value: string) => string;
  lazyFormatter?: boolean;
  number?: boolean;
  trim?: boolean;
  lazy?: boolean;
  debounce?: number | string;
  ariaLabel?: string;
  autoComplete?: string;
  label?: string;
  errorMessage?: string;
  required?: boolean;
  onChange?: (value: any, event?: React.ChangeEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const _ErpFormInput = forwardRef<HTMLInputElement, ErpFormInputProps>(({
  value: modelValue = '',
  type = 'text',
  placeholder,
  disabled = false,
  readOnly = false,
  maxLength,
  size,
  state = null,
  id,
  formatter,
  lazyFormatter = false,
  number = false,
  trim = false,
  lazy = false,
  debounce,
  ariaLabel,
  autoComplete,
  onChange,
  onBlur,
  onFocus,
  className,
  isInvalid,
  isValid,
  label,
  errorMessage,
  ...attrs
}, ref) => {
  const { t } = useTranslation();
  const generatedId = useId();
  const inputId = id || (label || errorMessage ? generatedId : undefined);
  const errorId = `${inputId}-error`;
  const internalRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => internalRef.current!);

  const sizeClass = size === 'sm' ? 'h-7 text-xs' : size === 'lg' ? 'h-9 text-base' : '';
  const isInvalidState = state === false || isInvalid || !!errorMessage;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFireRef = useRef<(() => void) | null>(null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Emit (and forget) a pending debounced change so the parent never loses
  // the last typed value on blur or unmount.
  const flushPending = () => {
    clearTimer();
    const fire = pendingFireRef.current;
    pendingFireRef.current = null;
    fire?.();
  };

  useEffect(() => {
    return () => {
      flushPending();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const format = (raw: string) => {
    if (formatter) return formatter(raw);
    return raw;
  };

  const coerce = (raw: string): string | number => {
    let v = raw;
    if (trim) v = v.trim();
    if (number) {
      if (v === '') return v;
      const n = Number(v);
      return Number.isNaN(n) ? v : n;
    }
    return v;
  };

  const emitValue = (raw: string, nativeEvent: React.ChangeEvent<HTMLInputElement>, eager: boolean) => {
    const shouldFormat = formatter && (!lazyFormatter || !eager);
    const formatted = shouldFormat ? format(raw) : raw;
    const value = coerce(formatted);

    const fire = () => {
      onChange?.(value, nativeEvent);
      if (shouldFormat && formatted !== raw) {
        if (internalRef.current) internalRef.current.value = formatted;
      }
    };

    // debounce is `number | string`; a CSS-style value such as '300ms'
    // yields NaN, which would silently disable debouncing altogether.
    const parsedDebounce = debounce !== undefined ? Number(debounce) : 0;
    const ms = Number.isFinite(parsedDebounce) && parsedDebounce > 0 ? parsedDebounce : 0;
    if (ms > 0) {
      clearTimer();
      pendingFireRef.current = fire;
      timerRef.current = setTimeout(() => {
        pendingFireRef.current = null;
        fire();
      }, ms);
    } else {
      fire();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (lazy) return;
    const raw = e.target.value;
    emitValue(raw, e, true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    flushPending();
    const raw = e.target.value;
    let finalRaw = raw;
    if (formatter && lazyFormatter) finalRaw = format(raw);
    const value = coerce(finalRaw);
    if (formatter && lazyFormatter && finalRaw !== raw) {
      if (internalRef.current) internalRef.current.value = finalRaw;
    }
    if (lazy) {
      onChange?.(value, e);
    }
    onBlur?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(e);
  };

  const input = (
    <Input
      ref={internalRef}
      {...attrs}
      id={inputId}
      type={type}
      value={modelValue ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      maxLength={maxLength}
      aria-label={ariaLabel || attrs['aria-label']}
      aria-describedby={[attrs['aria-describedby'], errorMessage ? errorId : null].filter(Boolean).join(' ') || undefined}
      autoComplete={autoComplete}
      aria-invalid={isInvalidState ? true : state === true || isValid ? false : attrs['aria-invalid'] ?? isInvalidState}
      className={[sizeClass, className].filter(Boolean).join(' ')}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
  if (!label && !errorMessage) return input;
  return (
    <ErpDiv className="min-w-0 space-y-2">
      {label && <ErpLabelRaw htmlFor={inputId} className="text-sm font-medium text-foreground">{label}{attrs.required && <ErpSpan aria-hidden="true" className="ms-1 text-destructive">*</ErpSpan>}</ErpLabelRaw>}
      {input}
      {errorMessage && <ErpP id={errorId} role="alert" className="text-xs text-red-700 dark:text-red-300">{translateApiErrorMessage(errorMessage, t)}</ErpP>}
    </ErpDiv>
  );
});

export const ErpFormInput = React.memo(_ErpFormInput);
